import confetti from 'canvas-confetti';

/**
 * Arrival celebration for the Onboarding Support board (Nic 2026-08-14): a
 * quick burst when a new lead lands in Intake.
 *
 * Wrapped rather than calling canvas-confetti from the board directly, so the
 * three suppression rules below live in ONE place and cannot be forgotten at
 * a future call site.
 */

// Board palette (sky / violet / emerald / amber). Mid-tone on purpose: these
// have to read against both the light board and the dark theme.
const COLORS = ['#38BDF8', '#8B5CF6', '#10B981', '#F59E0B'];

const THROTTLE_MS = 2000;
let lastFiredAt = 0;

// Arrivals that landed while the tab was hidden. Deliberately a FLAG, not a
// counter: five leads arriving while you were away is still one "welcome
// back" burst, not five stacked on top of each other.
let pendingArrival = false;

const reduceMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * A small burst of stars from one spot (Nic 2026-11-03, the scoreboard
 * chip): a dozen star particles, half a second, from where the chip sits.
 * Not confetti (confetti is a lead thing): stars, gold and violet, and
 * nothing full-screen. Same guards as the arrival burst. `at` is the
 * element to burst from; nothing to point at means no burst.
 */
export const fireStarBurst = (at) => {
  if (reduceMotion()) return false;
  if (typeof document !== 'undefined' && document.hidden) return false;
  const r = at?.getBoundingClientRect?.();
  if (!r || !window.innerWidth || !window.innerHeight) return false;
  confetti({
    particleCount: 14,
    // Down and out to both sides (Nic 2026-11-03): the chip sits at the top
    // of the screen, so an upward burst was mostly off-screen.
    angle: 270,
    spread: 180,
    startVelocity: 22,
    ticks: 70,
    gravity: 0.9,
    decay: 0.92,
    scalar: 0.9,
    shapes: ['star'],
    colors: ['#F59E0B', '#FBBF24', '#8B5CF6', '#A78BFA'],
    origin: { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight },
    zIndex: 2000,
    disableForReducedMotion: true,
  });
  return true;
};

/**
 * Two side bursts angled toward the middle. Fires at most once per 2s.
 *
 * Returns true when it actually fired — only so callers/tests can tell the
 * difference between "celebrated" and "suppressed".
 */
export const fireArrivalConfetti = () => {
  // A full-screen particle burst is exactly what this OS setting is for.
  if (reduceMotion()) return false;
  // HIDDEN TAB: defer, don't drop. Browsers throttle (or fully pause)
  // requestAnimationFrame in a hidden tab, so firing now would stall the
  // animation mid-flight and resume it minutes later, unrelated to anything
  // on screen. Remembered instead, and played once on return.
  //
  // Note this is TAB visibility, not window focus: a board parked on a second
  // monitor is `visible` and celebrates normally, which is the case that
  // actually matters.
  if (typeof document !== 'undefined' && document.hidden) {
    pendingArrival = true;
    return false;
  }
  // Arrivals can land several-per-second (a backlog sweep, a partner batch).
  // Ten overlapping bursts is not ten times the celebration.
  const now = Date.now();
  if (now - lastFiredAt < THROTTLE_MS) return false;
  lastFiredAt = now;

  // The knobs, if this ever needs retuning: particleCount = how much,
  // spread = how wide the cone, startVelocity = how far it throws,
  // ticks = how long before particles despawn, scalar = piece size.
  const base = {
    particleCount: 110,
    spread: 85,
    startVelocity: 62,
    ticks: 200,
    gravity: 1.05,
    scalar: 1.1,
    colors: COLORS,
    // Above the board's overlays, below nothing that matters. canvas-confetti
    // sets pointer-events:none on its canvas, so this never eats a click on
    // the tile that just appeared.
    zIndex: 2000,
    disableForReducedMotion: true,
  };
  // From both lower corners, angled inward — the tile appears in the middle
  // of the board, so a center-origin burst would fire behind it.
  confetti({ ...base, origin: { x: 0.08, y: 0.9 }, angle: 60 });
  confetti({ ...base, origin: { x: 0.92, y: 0.9 }, angle: 120 });
  return true;
};

/**
 * Play the deferred burst for arrivals that landed while the tab was hidden.
 * Call on visibilitychange. No-op when nothing was missed.
 *
 * The pending flag is only cleared when the burst ACTUALLY fires, so a flush
 * swallowed by the throttle (tab-flapping) doesn't silently eat the
 * celebration — the next return plays it.
 */
export const flushPendingCelebration = () => {
  if (!pendingArrival) return false;
  const fired = fireArrivalConfetti();
  if (fired) pendingArrival = false;
  return fired;
};

/** Leaving the board discards anything owed — it is a board celebration. */
export const clearPendingCelebration = () => {
  pendingArrival = false;
};

/** Test seam: lets a suite fire twice without waiting out the throttle. */
export const resetCelebrateThrottle = () => {
  lastFiredAt = 0;
  pendingArrival = false;
};
